import { useCallback, useMemo } from 'react'
import { NetworkStatus, useQuery, WatchQueryFetchPolicy } from '@apollo/client'
import { showApiErrorToast } from '@npco/zeller-design-system'

import {
  GetCustomerSites,
  GetSites,
} from 'graphql/merchant-portal/queries/sites'
import { rvNewSites } from 'graphql/reactiveVariables'
import { CustomerType } from 'hooks/useCustomerQuery/graphql/getCustomer.types'
import { useLoggedInUser } from 'hooks/useLoggedInUser'
import { getStorageDeduplicatedSites } from 'utils/newSitesHelper'
import { CustomerRole } from 'types/gql-global-types'
import {
  GetCustomerSites as GetCustomerSitesType,
  GetCustomerSitesVariables,
} from 'types/gql-types/GetCustomerSites'
import {
  GetSites as GetSitesType,
  GetSites_getSites_sites as SiteData,
  GetSitesVariables,
} from 'types/gql-types/GetSites'
import { SiteCache } from 'types/site'

interface UseGetSitesArguments {
  pageSize?: number
  fetchPolicy?: WatchQueryFetchPolicy
  nextFetchPolicy?: WatchQueryFetchPolicy
}

const SITES_QUERY_DEFAULT_PAGE_SIZE = 30

export const useGetSites = ({
  pageSize = SITES_QUERY_DEFAULT_PAGE_SIZE,
  fetchPolicy = 'network-only',
  nextFetchPolicy = 'cache-first',
}: UseGetSitesArguments = {}) => {
  const { userRole, userData } = useLoggedInUser()

  const isAdmin = useMemo(() => userRole === CustomerRole.ADMIN, [userRole])

  const variables = useMemo(
    () =>
      isAdmin
        ? { limit: pageSize }
        : { customerUuid: (userData as CustomerType).id },
    [isAdmin, pageSize, userData]
  )
  const query = isAdmin ? GetSites : GetCustomerSites
  const queryKey = isAdmin ? 'getSites' : 'getCustomer'

  const { data, fetchMore, networkStatus, error, refetch } = useQuery<
    GetSitesType | GetCustomerSitesType,
    GetSitesVariables | GetCustomerSitesVariables
  >(query, {
    fetchPolicy,
    errorPolicy: 'all',
    nextFetchPolicy,
    variables,
    onError: (err) => {
      showApiErrorToast(err)
    },
    notifyOnNetworkStatusChange: true,
  })

  const onLoadMore = useCallback(async () => {
    if (!isAdmin || !data) {
      return null
    }

    const { nextToken } = (data as GetSitesType).getSites

    if (!nextToken?.type) {
      return null
    }

    return fetchMore?.({
      variables: {
        ...variables,
        nextToken,
      } as GetSitesVariables,
    })
  }, [data, fetchMore, isAdmin, variables])

  const sites = useMemo<SiteData[]>(() => {
    if (!data) {
      return []
    }

    const newSitesData: SiteCache[] | undefined = rvNewSites()
    // INFO: ZD-1208 improve implementation to achieve TS requirements
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return getStorageDeduplicatedSites(newSitesData, data[queryKey].sites)
  }, [data, queryKey])

  const hasMore =
    isAdmin && Boolean((data as GetSitesType)?.getSites?.nextToken)

  const isLoading =
    networkStatus === NetworkStatus.loading ||
    networkStatus === NetworkStatus.refetch

  const isLoadingMore = networkStatus === NetworkStatus.fetchMore

  return {
    data,
    sites,
    isLoading,
    isLoadingMore,
    onLoadMore,
    hasMore,
    error,
    refetch,
  }
}
